import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";

import { db } from "../FirebaseConfig";
import { buildForeflightText } from "../lib/foreflight";
import type { AeropuertoDoc, FlightPaxEntry } from "../lib/types";

/**
 * Tarjeta "Texto para ForeFlight": arma la línea de observaciones del plan
 * de vuelo (activación/cancelación con el contacto de cada aeródromo sin
 * torre + nombre y RUT de cada pax) y la deja copiable con un toque.
 */
export function ForeflightCard({
  originIcao,
  destinationIcao,
  pax,
}: {
  originIcao: string;
  destinationIcao: string;
  pax: FlightPaxEntry[];
}) {
  const [contacts, setContacts] = useState<{ o: string | null; d: string | null } | null>(null);
  const [copied, setCopied] = useState<"ok" | "fail" | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([getDoc(doc(db, "Aeropuertos", originIcao)), getDoc(doc(db, "Aeropuertos", destinationIcao))])
      .then(([o, d]) => {
        if (!alive) return;
        setContacts({
          o: (o.data() as AeropuertoDoc | undefined)?.fplContacto ?? null,
          d: (d.data() as AeropuertoDoc | undefined)?.fplContacto ?? null,
        });
      })
      .catch(() => alive && setContacts({ o: null, d: null }));
    return () => {
      alive = false;
    };
  }, [originIcao, destinationIcao]);

  if (!contacts) return null;
  const text = buildForeflightText({ pax, origenContacto: contacts.o, destinoContacto: contacts.d });
  if (!text) return null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied("ok");
    } catch {
      setCopied("fail");
    }
    setTimeout(() => setCopied(null), 2500);
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-medium text-slate-900">Texto para ForeFlight</h2>
          <p className="text-xs text-slate-500">Observaciones del plan de vuelo: activación, cancelación y pasajeros con RUT.</p>
        </div>
        <button
          type="button"
          onClick={() => void copy()}
          className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-900"
        >
          {copied === "ok" ? "Copiado ✓" : copied === "fail" ? "No se pudo copiar" : "Copiar"}
        </button>
      </div>
      <p
        className="mt-3 select-all break-words rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[13px] leading-relaxed text-slate-800"
        onClick={(e) => {
          const range = document.createRange();
          range.selectNodeContents(e.currentTarget);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        }}
      >
        {text}
      </p>
      {!contacts.o && !contacts.d ? (
        <p className="mt-2 text-[11px] text-slate-400">
          Sin frase de activación: ninguno de los dos aeródromos tiene "Contacto plan de vuelo" en Admin → Aeródromos.
        </p>
      ) : null}
    </section>
  );
}
